import React from 'react';
import {useIsInternalDomain} from '@stripe-internal/connect-embedded-lib';
import type {ConnectElementAttributes} from '../../connect/ConnectElementAttributes';
import type {ConnectHtmlAttributeType} from '../../connect/ConnectJSInterface/HtmlAttributes';
import type {ConnectElementImportKeys} from '../../connect/ConnectJSInterface/ConnectElementList';
import {CONNECT_ELEMENT_IMPORTS} from '../../connect/ConnectJSInterface/ConnectElementList';
import {getStronglyTypedKeys} from '../../utils/getStronglyTypedKeys';

export const AttributeContext = React.createContext<
  Partial<Record<ConnectHtmlAttributeType, string>>
>({});
AttributeContext.displayName = 'AttributeContext';

export type Props = {
  attributes: ConnectElementAttributes;
  component: ConnectElementImportKeys;
};

export const AttributeProvider: React.FC<Props> = ({
  children,
  attributes,
  component,
}) => {
  const componentConfig = CONNECT_ELEMENT_IMPORTS[component];

  const [currentAttributes, setCurrentAttributes] = React.useState<
    Partial<Record<ConnectHtmlAttributeType, string>>
  >(attributes.values);

  const isInternal = useIsInternalDomain();

  const filteredAttributes = {...currentAttributes};
  getStronglyTypedKeys(filteredAttributes).forEach((key) => {
    const shouldDeleteKey = (() => {
      // If there are no html attributes registered
      if (!componentConfig.htmlAttributes) {
        return true;
      }

      // Or if the html attribute is not registered
      const htmlAttributeConfig = componentConfig.htmlAttributes[key];
      if (!htmlAttributeConfig) {
        return true;
      }

      // Or if the html attribute is internal and the session is not interal
      if (htmlAttributeConfig.isInternal && !isInternal) {
        return true;
      }

      return false;
    })();

    if (shouldDeleteKey) {
      delete filteredAttributes[key];
    }
  });

  // When the deferred auth promise completes, we set auth context in state/context
  React.useEffect(() => {
    const observer = () => {
      setCurrentAttributes(attributes.values);
    };
    attributes.registerObserver(observer);
    return () => {
      attributes.unregisterObserver(observer);
    };
  }, [attributes]);

  return (
    <AttributeContext.Provider value={filteredAttributes}>
      {children}
    </AttributeContext.Provider>
  );
};
